import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Badge, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import { fetchActiveUserSessions } from '../services/api'

export default function Observability() {
  const [activeWindowMinutes, setActiveWindowMinutes] = useState(15)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['observability-sessions', activeWindowMinutes],
    queryFn: () => fetchActiveUserSessions(activeWindowMinutes),
    refetchInterval: 15000,
  })

  return (
    <Container fluid>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="mb-1">Observability</h1>
          <p className="text-muted mb-0">Live view of authenticated users observed by the API.</p>
        </div>
        <Form.Select
          value={activeWindowMinutes}
          onChange={(event) => setActiveWindowMinutes(Number(event.target.value))}
          style={{ maxWidth: 220 }}
          aria-label="Active user window"
        >
          <option value={5}>Active in last 5 minutes</option>
          <option value={15}>Active in last 15 minutes</option>
          <option value={30}>Active in last 30 minutes</option>
          <option value={60}>Active in last 60 minutes</option>
        </Form.Select>
      </div>

      {isError && <Alert variant="danger">Failed to load observability data.</Alert>}

      {isLoading && (
        <div className="text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading active sessions...
        </div>
      )}

      {data && (
        <>
          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card body>
                <div className="small text-muted">Active users</div>
                <div className="display-6 fw-semibold">{data.activeUserCount}</div>
              </Card>
            </Col>
            <Col md={4}>
              <Card body>
                <div className="small text-muted">Active sessions</div>
                <div className="display-6 fw-semibold">{data.activeSessionCount}</div>
              </Card>
            </Col>
            <Col md={4}>
              <Card body>
                <div className="small text-muted">Observed sessions today</div>
                <div className="display-6 fw-semibold">{data.observedSessionCount}</div>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span>Logged-in users</span>
              <span className="small text-muted">Updated {formatDateTime(data.generatedUtc)}</span>
            </Card.Header>
            <Card.Body>
              {data.sessions.length === 0 ? (
                <Alert variant="light" className="mb-0">
                  No active authenticated API sessions were seen in the selected window.
                </Alert>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Auth</th>
                      <th>First seen</th>
                      <th>Last seen</th>
                      <th>Active for</th>
                      <th>Last API path</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sessions.map((session) => (
                      <tr key={session.sessionKey}>
                        <td>
                          <div className="fw-semibold">{session.displayName}</div>
                          <div className="small text-muted">{session.email || session.userId}</div>
                        </td>
                        <td>
                          <Badge bg={session.authMethod === 'Entra ID' ? 'primary' : 'secondary'}>
                            {session.authMethod || 'Unknown'}
                          </Badge>
                        </td>
                        <td>{formatDateTime(session.firstSeenUtc)}</td>
                        <td>{formatDateTime(session.lastSeenUtc)}</td>
                        <td>{formatDuration(session.durationSeconds)}</td>
                        <td className="text-muted small">{session.lastPath || '-'}</td>
                        <td className="text-muted small">{session.remoteIpAddress || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  )
}

function formatDateTime(value?: string) {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

function formatDuration(totalSeconds: number) {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`
  return `${remainingSeconds}s`
}
